import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { StarDisplay, StarRating } from "@/components/StarRating";
import { toast } from "sonner";
import { Copy, Star, Clock, Info } from "lucide-react";

function fmt(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  if (isNaN(d)) return dt;
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

/**
 * RatingsModal — shown on Rides page for completed rides.
 * Displays blind two-way ratings status. If `canRateAsDriver` is true (driver portal),
 * the driver can submit their rating. For agency/client, provides "copy rating link".
 */
export default function RatingsModal({ rideId, ride, canRateAsDriver = false, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stars, setStars] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/rides/${rideId}/ratings`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [rideId]);

  const submit = async () => {
    if (stars < 1) return toast.error("Select a star rating");
    setSubmitting(true);
    try {
      const { data: d } = await api.post(`/driver/rides/${rideId}/rate`, { stars, comment });
      setData(d);
      setStars(0);
      setComment("");
      toast.success("Rating submitted");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const copyLink = async () => {
    if (!data?.rating_token) return;
    const url = `${window.location.origin}/rate/${data.rating_token}`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Rating link copied to clipboard");
    } catch {
      window.prompt("Copy this rating link:", url);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="ratings-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full flex flex-col max-h-[90vh]">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-semibold text-slate-900">Trip ratings</div>
            {ride && (
              <div className="text-xs text-slate-500 mt-0.5">
                {ride.patient_name} · <Clock size={11} className="inline mb-0.5" /> {fmt(ride.pickup_datetime)}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-500 text-sm hover:text-slate-900" data-testid="ratings-close">Close</button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {loading && <div className="text-sm text-slate-500 text-center py-6">Loading…</div>}

          {!loading && data && (
            <>
              <StatusBanner status={data.rating_status} />

              <div className="grid grid-cols-2 gap-3">
                <RatingCard
                  title="Driver → Rider"
                  submitted={data.driver_submitted}
                  rating={data.driver_rating}
                  bothVisible={data.rating_status === "both_submitted"}
                />
                <RatingCard
                  title="Rider → Driver"
                  submitted={data.rider_submitted}
                  rating={data.rider_rating}
                  bothVisible={data.rating_status === "both_submitted"}
                />
              </div>

              {canRateAsDriver && !data.driver_submitted && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Rate this rider</div>
                  <div className="flex justify-center py-1">
                    <StarRating value={stars} onChange={setStars} size={30} testId="driver-stars" />
                  </div>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    rows={3}
                    maxLength={1000}
                    placeholder="Comments (optional)"
                    className="mt-3 w-full text-sm px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-300"
                    data-testid="driver-comment"
                  />
                  <button
                    onClick={submit}
                    disabled={submitting || stars < 1}
                    className="mt-3 w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-sm rounded-md py-2.5 font-semibold"
                    data-testid="driver-rate-submit"
                  >
                    {submitting ? "Submitting…" : "Submit my rating"}
                  </button>
                  <div className="text-[10px] text-slate-400 text-center mt-2">
                    Ratings are blind — you'll see the rider's feedback once they submit theirs.
                  </div>
                </div>
              )}

              {!canRateAsDriver && data.rating_token && !data.rider_submitted && (
                <div className="border-t border-slate-100 pt-4">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Share rider link</div>
                  <div className="text-xs text-slate-600 mb-2">
                    Send this link to the customer to collect their rating. No login required.
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      readOnly
                      value={`${window.location.origin}/rate/${data.rating_token}`}
                      className="flex-1 text-xs font-mono px-2 py-1.5 rounded-md border border-slate-200 bg-slate-50"
                      data-testid="rating-link"
                    />
                    <button onClick={copyLink} className="btn-primary text-xs inline-flex items-center gap-1" data-testid="copy-rating-link">
                      <Copy size={12} /> Copy
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusBanner({ status }) {
  const map = {
    both_submitted: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", text: "Both ratings submitted", icon: <Star size={13} /> },
    waiting_for_both: { cls: "bg-slate-50 text-slate-700 border-slate-200", text: "Waiting for both parties to rate", icon: <Info size={13} /> },
    waiting_for_rider: { cls: "bg-amber-50 text-amber-800 border-amber-200", text: "Waiting for the rider to submit", icon: <Info size={13} /> },
    waiting_for_driver: { cls: "bg-amber-50 text-amber-800 border-amber-200", text: "Waiting for the driver to submit", icon: <Info size={13} /> },
  };
  const m = map[status] || map.waiting_for_both;
  return (
    <div className={`text-xs px-3 py-2 rounded-md border inline-flex items-center gap-1.5 ${m.cls}`} data-testid={`rating-status-${status}`}>
      {m.icon} {m.text}
    </div>
  );
}

function RatingCard({ title, submitted, rating, bothVisible }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{title}</div>
      <div className="mt-2">
        {!submitted && <div className="text-xs text-slate-400 italic">Not submitted yet</div>}
        {submitted && !bothVisible && (
          <div className="text-xs text-emerald-700 font-medium">Submitted · hidden until both rate</div>
        )}
        {submitted && bothVisible && rating && (
          <>
            <StarDisplay stars={rating.stars} size={14} />
            {rating.comment && (
              <div className="text-xs text-slate-700 mt-2 italic">"{rating.comment}"</div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
